/*
 * Copyright 2023 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';
import { YorkieError, Code } from '@yorkie-js/sdk/src/util/error';

/**
 * `ElementRHTNode` is a node of ElementRHT.
 */
export class ElementRHTNode {
  private strKey: string;
  private value: CRDTElement;

  constructor(strKey: string, value: CRDTElement) {
    this.strKey = strKey;
    this.value = value;
  }

  /**
   * `of` creates a instance of ElementRHTNode.
   */
  public static of(strKey: string, value: CRDTElement): ElementRHTNode {
    return new ElementRHTNode(strKey, value);
  }

  /**
   * `isRemoved` checks whether this value was removed.
   */
  public isRemoved(): boolean {
    return this.value.isRemoved();
  }

  /**
   * `getStrKey` returns the key of this node.
   */
  public getStrKey(): string {
    return this.strKey;
  }

  /**
   * `getValue` return the value(element) of this node
   */
  public getValue(): CRDTElement {
    return this.value;
  }

  /**
   * `remove` removes a value base on removing time.
   */
  public remove(removedAt: TimeTicket): boolean {
    return this.value.remove(removedAt);
  }
}

/**
 * ElementRHT is a hashtable with logical clock(Replicated hashtable)
 *
 */
export class ElementRHT {
  private nodeMapByKey: Map<string, ElementRHTNode>;
  private nodeMapByCreatedAt: Map<string, ElementRHTNode>;

  constructor() {
    this.nodeMapByKey = new Map();
    this.nodeMapByCreatedAt = new Map();
  }

  /**
   * `create` creates an instance of ElementRHT.
   */
  public static create(): ElementRHT {
    return new ElementRHT();
  }

  /**
   * `set` sets the value of the given key. An existing occupant is removed
   * only when the incoming value wins the LWW comparison; when it loses, the
   * occupant stays and the incoming value is marked removed instead.
   *
   * Both the win/lose decision and the eviction of the previous occupant are
   * anchored on the occupant's `positionedAt` (its `movedAt`, falling back to
   * its `createdAt`). Anchoring them on different tickets lets them disagree:
   * `CRDTElement.remove` gates on the raw `createdAt`, so for an occupant
   * whose `createdAt < executedAt < positionedAt` — which is what an undo/redo
   * restore produces, since it re-places the original element under a fresh
   * ticket — the eviction fires and tombstones the occupant, while the winner
   * check decides the incoming value must NOT replace it. The occupant is then
   * tombstoned but still linked as the key's value, the incoming value is
   * dropped without being registered as removed, and `get` reports the key as
   * absent although no operation ever removed it.
   *
   * The inner `node.remove(executedAt)` gate is therefore redundant once the
   * eviction sits inside the winner branch — that branch already guarantees
   * `executedAt > positionedAt >= createdAt`, which is what
   * `CRDTElement.remove` checks. It is kept so this reads as the mirror of Go
   * that it is.
   *
   * The losing branch marks the incoming value removed only when it is not
   * removed already. The marking exists solely to keep a duplicate out of
   * `ownKeys`, and a value that arrives removed is already out; but
   * `CRDTElement.remove` accepts any ticket later than the one it holds, so
   * without the guard it is not a no-op. `converter.fromObject` replays a
   * decoded object through here, and a decoded tombstone that sorts after the
   * live occupant would have its `removedAt` bumped from the ticket of the
   * removal that actually happened to the occupant's `positionedAt` — making
   * GC on this replica wait on the wrong ticket, and its re-serialized
   * snapshots and `docSize.gc` disagree with replicas that never reloaded.
   *
   * That made rebuilding an object from a snapshot depend on the order its
   * members happened to arrive in — see
   * `test/unit/document/crdt/element_rht_order_test.ts`, and
   * `ElementRHT.SetWithExecutedAt` in `yorkie/pkg/document/crdt/element_rht.go`,
   * whose anchoring this now mirrors.
   */
  public set(
    key: string,
    value: CRDTElement,
    executedAt: TimeTicket,
  ): CRDTElement | undefined {
    let removed;
    const node = this.nodeMapByKey.get(key);

    const newNode = ElementRHTNode.of(key, value);
    this.nodeMapByCreatedAt.set(value.getCreatedAt().toIDString(), newNode);
    if (!node || executedAt.after(node.getValue().getPositionedAt())) {
      if (!!node && !node.isRemoved() && node.remove(executedAt)) {
        removed = node.getValue();
      }
      this.nodeMapByKey.set(key, newNode);
      value.setMovedAt(executedAt);
    } else if (!value.isRemoved()) {
      // A live incoming node that loses the LWW conflict must be removed even
      // when the current occupant is already a tombstone.
      value.remove(node.getValue().getPositionedAt());
    }
    return removed;
  }

  /**
   * `delete` deletes the Element of the given key.
   */
  public delete(createdAt: TimeTicket, executedAt: TimeTicket): CRDTElement {
    if (!this.nodeMapByCreatedAt.has(createdAt.toIDString())) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `fail to find ${createdAt.toIDString()}`,
      );
    }

    const node = this.nodeMapByCreatedAt.get(createdAt.toIDString())!;
    node.remove(executedAt);
    return node.getValue();
  }

  /**
   * `subPathOf` returns the sub path of the given element.
   */
  public subPathOf(createdAt: TimeTicket): string | undefined {
    const node = this.nodeMapByCreatedAt.get(createdAt.toIDString());
    if (!node) {
      return;
    }

    return node.getStrKey();
  }

  /**
   * `purge` physically purge child element.
   */
  public purge(element: CRDTElement): void {
    const node = this.nodeMapByCreatedAt.get(
      element.getCreatedAt().toIDString(),
    );
    if (!node) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `fail to find ${element.getCreatedAt().toIDString()}`,
      );
    }

    // The slot names a creation time, not an element. Undo of a remove
    // re-inserts a copy under the original createdAt and `set` re-points this
    // map at it, so unlinking whatever the key answers with would delete a live
    // member on a tombstone's behalf. The tombstone is already off both maps by
    // then, so there is nothing left to unlink.
    //
    // A genuinely missing key still throws above: that is a mis-registration
    // worth reporting, and this guard is only about a slot that has been taken
    // over.
    if (node.getValue() !== element) {
      return;
    }

    const nodeByKey = this.nodeMapByKey.get(node.getStrKey());
    if (node === nodeByKey) {
      this.nodeMapByKey.delete(nodeByKey.getStrKey());
    }

    this.nodeMapByCreatedAt.delete(node.getValue().getCreatedAt().toIDString());
  }

  /**
   * `deleteByKey` deletes the Element of the given key and removed time.
   */
  public deleteByKey(
    key: string,
    removedAt: TimeTicket,
  ): CRDTElement | undefined {
    const node = this.nodeMapByKey.get(key);
    if (!node) {
      return;
    }

    if (!node.remove(removedAt)) {
      return;
    }

    return node.getValue();
  }

  /**
   * `has` returns whether the element exists of the given key or not.
   */
  public has(key: string): boolean {
    const node = this.nodeMapByKey.get(key);
    if (!node) {
      return false;
    }
    return !node.isRemoved();
  }

  /**
   * `getByID` returns the node of the given createdAt.
   */
  public getByID(createdAt: TimeTicket): ElementRHTNode | undefined {
    return this.nodeMapByCreatedAt.get(createdAt.toIDString());
  }

  /**
   * `get` returns the node of the given key.
   */
  public get(key: string): ElementRHTNode | undefined {
    const node = this.nodeMapByKey.get(key);
    if (!node || node.isRemoved()) {
      return;
    }

    return node;
  }

  /**
   * `deepcopy` creates a deep copy of this ElementRHT.
   */
  public deepcopy(): ElementRHT {
    const clone = ElementRHT.create();
    for (const [, node] of this.nodeMapByCreatedAt) {
      clone.nodeMapByCreatedAt.set(
        node.getValue().getCreatedAt().toIDString(),
        ElementRHTNode.of(node.getStrKey(), node.getValue().deepcopy()),
      );
    }

    for (const [key, node] of this.nodeMapByKey) {
      clone.nodeMapByKey.set(
        key,
        clone.nodeMapByCreatedAt.get(
          node.getValue().getCreatedAt().toIDString(),
        )!,
      );
    }

    return clone;
  }

  // eslint-disable-next-line jsdoc/require-jsdoc
  public *[Symbol.iterator](): IterableIterator<ElementRHTNode> {
    for (const [, node] of this.nodeMapByCreatedAt) {
      yield node;
    }
  }
}
